import type { GetApiV1ThreeLetterCodeClientAdminsClientNewslettersId200 } from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetClientNewsletter,
  useGetClientNewsletterStatistics,
  useGetClientNewsletterUsers,
} from '~/hooks';
import { ROUTES } from '~/utils/routes';
import { InternalNewsDetailPresentation } from '../presentations';

type PresentationProps = React.ComponentProps<typeof InternalNewsDetailPresentation>;

type ActivePageTab = 'article' | 'viewRate';

type Args = {
  newsId: number;
};

type IUseInternalNewsDetail = (args: Args) => {
  state: {
    newsletterData: GetApiV1ThreeLetterCodeClientAdminsClientNewslettersId200;
    published: boolean;
    activePageTab: PresentationProps['activePageTab'];
    usersScope: PresentationProps['usersScope'];
    activeTab: PresentationProps['activeTab'];
    currentPage: number;
    statisticsData: ReturnType<typeof useGetClientNewsletterStatistics>['data'];
    usersData: ReturnType<typeof useGetClientNewsletterUsers>['data'];
    totalPage: number;
    totalCount: number;
  };
  actions: {
    handleEdit: () => void;
    handlePageTabChange: PresentationProps['onPageTabChange'];
    handleUsersScopeChange: PresentationProps['onUsersScopeChange'];
    handleTabChange: PresentationProps['onTabChange'];
    handlePageChange: PresentationProps['pagerProps']['onPageChange'];
  };
};

/**
 * 社内報詳細画面の状態管理とアクションを提供する
 */
export const useInternalNewsDetail: IUseInternalNewsDetail = ({ newsId }) => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode() || '';

  const { data: newsletterData } = useGetClientNewsletter({ threeLetterCode, id: newsId });

  const published = newsletterData.client_newsletter.is_published;

  const [activePageTab, setActivePageTab] = useState<ActivePageTab>('article');
  const [searchParams, setSearchParams] = useSearchParams();

  const usersScope = searchParams.get('scope') === 'all' ? 'all' : 'at_publication';

  const activeTab = searchParams.get('readStatus') === 'read' ? 'read' : 'unread';

  const currentPage = Number(searchParams.get('page')) || 1;

  const { data: statisticsData } = useGetClientNewsletterStatistics({
    threeLetterCode,
    id: newsId,
    scope: usersScope,
    enabled: published,
  });

  const {
    data: usersData,
    totalPage,
    totalCount,
  } = useGetClientNewsletterUsers({
    threeLetterCode,
    id: newsId,
    scope: usersScope,
    readStatus: activeTab,
    page: currentPage,
    enabled: published,
  });

  /**
   * 社内報編集画面へ遷移する
   */
  const handleEdit = () => {
    navigate(ROUTES.INTERNAL_NEWS.EDIT.replace(':id', String(newsId)));
  };

  /**
   * ページタブを変更する
   */
  const handlePageTabChange: PresentationProps['onPageTabChange'] = tab => {
    setActivePageTab(tab);
  };

  /**
   * 母数スコープを変更し、ページネーションをリセットする
   */
  const handleUsersScopeChange: PresentationProps['onUsersScopeChange'] = scope => {
    setSearchParams(prev => {
      prev.set('scope', scope);
      prev.set('page', '1');
      return prev;
    });
  };

  /**
   * 未読/既読タブを変更し、ページネーションをリセットする
   */
  const handleTabChange: PresentationProps['onTabChange'] = tab => {
    setSearchParams(prev => {
      prev.set('readStatus', tab);
      prev.set('page', '1');
      return prev;
    });
  };

  /**
   * ページネーションのページを変更する
   */
  const handlePageChange: PresentationProps['pagerProps']['onPageChange'] = page => {
    setSearchParams(prev => {
      prev.set('page', String(page));
      return prev;
    });
  };

  return {
    state: {
      newsletterData,
      published,
      activePageTab,
      usersScope,
      activeTab,
      currentPage,
      statisticsData,
      usersData,
      totalPage,
      totalCount,
    },
    actions: {
      handleEdit,
      handlePageTabChange,
      handleUsersScopeChange,
      handleTabChange,
      handlePageChange,
    },
  };
};
